import { z } from "zod";
import { zEmail, zId, zIsoDate } from "./common.js";
import { zLanguage } from "./i18n.js";

export const CONSTITUENT_TYPES = ["individual", "household", "organization", "foundation"] as const;
export const zConstituentType = z.enum(CONSTITUENT_TYPES);
export type ConstituentType = z.infer<typeof zConstituentType>;

export const CONTACT_CHANNELS = ["email", "phone", "mail", "text"] as const;
export const zContactChannel = z.enum(CONTACT_CHANNELS);

export const CONTACT_PURPOSES = ["solicitation", "stewardship"] as const;
export const zContactPurpose = z.enum(CONTACT_PURPOSES);

export const zEmailAddress = z.object({
  id: zId.optional(),
  address: zEmail,
  type: z.enum(["home", "work", "other"]).default("home"),
  isPrimary: z.boolean().default(false),
});
export type EmailAddress = z.infer<typeof zEmailAddress>;

export const zPhoneNumber = z.object({
  id: zId.optional(),
  number: z.string().trim().min(3).max(30),
  type: z.enum(["home", "work", "mobile", "other"]).default("mobile"),
  isPrimary: z.boolean().default(false),
});
export type PhoneNumber = z.infer<typeof zPhoneNumber>;

export const zAddress = z.object({
  id: zId.optional(),
  line1: z.string().trim().max(200).default(""),
  line2: z.string().trim().max(200).default(""),
  city: z.string().trim().max(100).default(""),
  state: z.string().trim().max(100).default(""),
  postalCode: z.string().trim().max(20).default(""),
  country: z.string().trim().max(2).default("US"),
  type: z.enum(["home", "work", "seasonal", "other"]).default("home"),
  isPrimary: z.boolean().default(false),
  seasonalStartMonthDay: z.string().regex(/^\d{2}-\d{2}$/).nullable().optional(),
  seasonalEndMonthDay: z.string().regex(/^\d{2}-\d{2}$/).nullable().optional(),
});
export type Address = z.infer<typeof zAddress>;

export const zDoNotContact = z.object({
  channel: zContactChannel,
  purpose: zContactPurpose.nullable().describe("null = all purposes"),
});
export type DoNotContact = z.infer<typeof zDoNotContact>;

export const zConstituent = z.object({
  id: zId,
  type: zConstituentType,
  displayName: z.string(),
  sortName: z.string(),
  // individual fields
  prefix: z.string().nullable(),
  firstName: z.string().nullable(),
  middleName: z.string().nullable(),
  lastName: z.string().nullable(),
  suffix: z.string().nullable(),
  nickname: z.string().nullable(),
  birthDate: zIsoDate.nullable(),
  deceased: z.boolean(),
  deceasedDate: zIsoDate.nullable(),
  /** opted out of public recognition: never listed on donor walls */
  recognitionOptOut: z.boolean(),
  /** CT-RM-30: language for receipts, acknowledgments and letters; null = the org default */
  preferredLanguage: zLanguage.nullable().optional(),
  // org fields
  orgName: z.string().nullable(),
  website: z.string().nullable(),
  // household
  householdId: zId.nullable(),
  // contact info
  emails: z.array(zEmailAddress),
  phones: z.array(zPhoneNumber),
  addresses: z.array(zAddress),
  doNotContact: z.array(zDoNotContact),
  // meta
  tags: z.array(z.string()),
  customFields: z.record(z.unknown()),
  sourceIds: z.record(z.string()).describe("legacy system id map, e.g. {raisers_edge: '1234'}"),
  createdAt: z.string(),
  updatedAt: z.string(),
  // rollups (read-only)
  lifetimeGiving: z.number().int().optional(),
  lastGiftDate: zIsoDate.nullable().optional(),
  lastGiftAmount: z.number().int().nullable().optional(),
  firstGiftDate: zIsoDate.nullable().optional(),
  giftCount: z.number().int().optional(),
});
export type Constituent = z.infer<typeof zConstituent>;

export const zCreateConstituent = z.object({
  type: zConstituentType,
  prefix: z.string().trim().max(30).optional(),
  firstName: z.string().trim().max(100).optional(),
  middleName: z.string().trim().max(100).optional(),
  lastName: z.string().trim().max(100).optional(),
  suffix: z.string().trim().max(30).optional(),
  nickname: z.string().trim().max(100).optional(),
  birthDate: zIsoDate.optional(),
  orgName: z.string().trim().max(200).optional(),
  website: z.string().trim().max(300).optional(),
  householdId: zId.optional(),
  preferredLanguage: zLanguage.optional(),
  emails: z.array(zEmailAddress).default([]),
  phones: z.array(zPhoneNumber).default([]),
  addresses: z.array(zAddress).default([]),
  doNotContact: z.array(zDoNotContact).default([]),
  tags: z.array(z.string().trim().min(1).max(60)).default([]),
  customFields: z.record(z.unknown()).default({}),
  sourceIds: z.record(z.string()).default({}),
  /** skip duplicate detection (e.g. user confirmed "create anyway") */
  ignoreDuplicates: z.boolean().default(false),
}).superRefine((val, ctx) => {
  if (val.type === "individual" && !val.firstName && !val.lastName) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["firstName"], message: "Enter a first or last name" });
  }
  if ((val.type === "organization" || val.type === "foundation" || val.type === "household") && !val.orgName && !val.lastName) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["orgName"], message: "Enter a name" });
  }
});
export type CreateConstituent = z.infer<typeof zCreateConstituent>;

export const zUpdateConstituent = z.object({
  prefix: z.string().trim().max(30).nullable().optional(),
  firstName: z.string().trim().max(100).nullable().optional(),
  middleName: z.string().trim().max(100).nullable().optional(),
  lastName: z.string().trim().max(100).nullable().optional(),
  suffix: z.string().trim().max(30).nullable().optional(),
  nickname: z.string().trim().max(100).nullable().optional(),
  birthDate: zIsoDate.nullable().optional(),
  deceased: z.boolean().optional(),
  deceasedDate: zIsoDate.nullable().optional(),
  recognitionOptOut: z.boolean().optional(),
  preferredLanguage: zLanguage.nullable().optional(),
  orgName: z.string().trim().max(200).nullable().optional(),
  website: z.string().trim().max(300).nullable().optional(),
  householdId: zId.nullable().optional(),
  emails: z.array(zEmailAddress).optional(),
  phones: z.array(zPhoneNumber).optional(),
  addresses: z.array(zAddress).optional(),
  doNotContact: z.array(zDoNotContact).optional(),
  tags: z.array(z.string().trim().min(1).max(60)).optional(),
  customFields: z.record(z.unknown()).optional(),
});
export type UpdateConstituent = z.infer<typeof zUpdateConstituent>;

// relationships
export const zRelationship = z.object({
  id: zId,
  fromConstituentId: zId,
  toConstituentId: zId,
  relationshipType: z.string(),
  reciprocalType: z.string().nullable(),
  notes: z.string().nullable(),
  toDisplayName: z.string().optional(),
  fromDisplayName: z.string().optional(),
});
export type Relationship = z.infer<typeof zRelationship>;

export const zCreateRelationship = z.object({
  toConstituentId: zId,
  relationshipType: z.string().trim().min(1).max(80),
  reciprocalType: z.string().trim().max(80).optional(),
  notes: z.string().max(2000).optional(),
});

// dedupe
export const zDuplicateMatch = z.object({
  constituentId: zId,
  displayName: z.string(),
  score: z.number().describe("0-100 confidence"),
  reasons: z.array(z.string()),
});
export type DuplicateMatch = z.infer<typeof zDuplicateMatch>;

export const zMergeRequest = z.object({
  winnerId: zId,
  loserId: zId,
  /** field name -> "winner" | "loser" for conflicting scalar fields */
  fieldChoices: z.record(z.enum(["winner", "loser"])).default({}),
});
export type MergeRequest = z.infer<typeof zMergeRequest>;
